import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  AppleOutlined,
  CheckCircleOutlined,
  GoogleOutlined,
  KeyOutlined,
  LinkedinOutlined,
  LockOutlined,
  MailOutlined,
  RocketOutlined,
  TeamOutlined,
  UserOutlined,
  WindowsOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Input, Progress, Space, Typography } from "antd";
import AuthBrand from "components/auth/AuthBrand";
import AuthShell from "components/auth/AuthShell";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { OLIVEEB_COLORS } from "workspaces/colors";

const { Paragraph, Text, Title } = Typography;

const starterStorageKey = "olreadiaInitialWorkspaceDraft";

const optionIconStyle = {
  fontSize: 18,
};

const questions = [
  {
    key: "useCase",
    eyebrow: "Your path",
    title: "How will you use Olreadia first?",
    deck: "This helps us shape your signup around the account you actually need.",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebBlue,
      title: OLIVEEB_COLORS.oliveebDarkBlue,
      deck: OLIVEEB_COLORS.oliveebBlue,
      accent: OLIVEEB_COLORS.oliveebBlue,
      soft: OLIVEEB_COLORS.oliveebBlueSoft,
    },
    options: [
      { value: "job_search", label: "For Job Search", caption: "Build trust, get verified early, and prepare for opportunities.", icon: <UserOutlined style={optionIconStyle} /> },
      { value: "hiring", label: "For Hiring", caption: "Find verified professionals and reduce avoidable hiring delays.", icon: <TeamOutlined style={optionIconStyle} /> },
    ],
  },
  {
    key: "hiringType",
    eyebrow: "Identity",
    title: "What best describes you?",
    deck: "Choose whether you are hiring as an individual or as an organization.",
    showIf: (answers) => answers.useCase === "hiring",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebGreen,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    options: [
      { value: "individual", label: "An Individual", caption: "I am hiring or preparing to hire on my own.", icon: <UserOutlined style={optionIconStyle} /> },
      { value: "organization", label: "Organization/Hiring Team", caption: "I am hiring for a company, team, or organization.", icon: <TeamOutlined style={optionIconStyle} /> },
    ],
  },
  {
    key: "workspaceName",
    eyebrow: "Organization",
    title: "What is the organization name?",
    deck: "Use the organization or team name that should appear in your hiring workspace.",
    showIf: (answers) => answers.useCase === "hiring" && answers.hiringType === "organization",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebGreen,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    input: {
      placeholder: "Organization or team name",
      maxLength: 80,
    },
  },
  {
    key: "deliveryFocus",
    eyebrow: "Priorities",
    title: "What do you want Olreadia to help with first?",
    deck: "Select every priority that should shape your first experience.",
    showIf: (answers) => answers.useCase === "job_search",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebOrange,
      title: OLIVEEB_COLORS.oliveebDarkRed,
      deck: OLIVEEB_COLORS.oliveebOrange,
      accent: OLIVEEB_COLORS.oliveebOrange,
      soft: OLIVEEB_COLORS.goldSoft,
    },
    multiple: true,
    options: [
      { value: "verified_profile", label: "Trusted professional profile", caption: "Create reusable proof of readiness and credibility.", icon: <CheckCircleOutlined style={optionIconStyle} /> },
      { value: "background_check", label: "Background-check readiness", caption: "Prepare verification context before a role needs to start.", icon: <RocketOutlined style={optionIconStyle} /> },
    ],
  },
  {
    key: "credentialMethod",
    eyebrow: "Login",
    title: "How would you like to create your login?",
    deck: "Choose a username and password, or continue with a social account.",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebBlue,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    options: [
      { value: "username_password", label: "Username and password", caption: "Create your Olreadia login with an email username and password.", icon: <KeyOutlined style={optionIconStyle} /> },
      {
        value: "social_login",
        label: "Social login",
        caption: "Use Google, Apple, Microsoft, or LinkedIn.",
        icon: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <GoogleOutlined style={optionIconStyle} />
            <AppleOutlined style={optionIconStyle} />
            <WindowsOutlined style={optionIconStyle} />
            <LinkedinOutlined style={optionIconStyle} />
          </span>
        ),
      },
    ],
  },
  {
    key: "loginCredentials",
    eyebrow: "Username and password",
    title: "Create your username and password",
    deck: "Use an email username and a password for your Olreadia login.",
    showIf: (answers) => answers.credentialMethod === "username_password",
    colors: {
      eyebrow: OLIVEEB_COLORS.oliveebGreen,
      title: OLIVEEB_COLORS.oliveebDarkGreen,
      deck: OLIVEEB_COLORS.oliveebBlue,
      accent: OLIVEEB_COLORS.oliveebGreen,
      soft: OLIVEEB_COLORS.oliveebGreenSoft,
    },
    credentialsForm: true,
  },
];

const initialAnswers = {
  email: "",
  password: "",
  confirmPassword: "",
  useCase: "",
  hiringType: "",
  usageType: "",
  workspaceName: "",
  firstName: "",
  lastName: "",
  deliveryFocus: [],
  credentialMethod: "",
  socialProvider: "",
};

const onboardingStyles = `
  .olreadia-onboarding-card {
    border-radius: 28px;
    background: linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(246,255,249,0.96) 58%, rgba(255,253,235,0.94) 100%);
    box-shadow: 0 26px 72px rgba(0, 82, 64, 0.14), inset 0 1px 0 rgba(255,255,255,0.95);
    border: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
    overflow: hidden;
  }
  .olreadia-onboarding-option {
    width: 100%;
    height: auto;
    min-height: 76px;
    padding: 12px 14px;
    border-radius: 20px;
    text-align: left;
    white-space: normal;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    box-shadow: 0 12px 24px rgba(16,147,87,0.08);
  }
  .olreadia-onboarding-input {
    height: 54px;
    border-radius: 999px !important;
    border-color: ${OLIVEEB_COLORS.oliveebGreenSoft} !important;
    font-size: 17px;
    font-weight: 800;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
    box-shadow: 0 14px 28px rgba(16,147,87,0.10);
  }
  .olreadia-onboarding-input.ant-input-affix-wrapper,
  .olreadia-onboarding-input.ant-input-password {
    border-radius: 999px !important;
    overflow: hidden;
  }
  .olreadia-onboarding-input .ant-input {
    font-size: 17px;
    font-weight: 800;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
  }
  .olreadia-onboarding-input:focus,
  .olreadia-onboarding-input-focused {
    border-color: ${OLIVEEB_COLORS.oliveebGreen} !important;
    box-shadow: 0 0 0 4px rgba(39,174,48,0.12), 0 14px 28px rgba(16,147,87,0.10) !important;
  }
`;

const getVisibleQuestions = (answers) =>
  questions.filter((question) => !question.showIf || question.showIf(answers));

const getVisibleOptions = (question) =>
  (question.options || []).filter(
    (option) => !option.audience,
  );

const normalizeMultiValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const formatLabelValue = (value) =>
  Array.isArray(value) ? value.filter(Boolean).join(", ") : value;

const getDerivedRole = (usageType) => {
  if (usageType === "hiring_team") {
    return { role: "recruiter", roleLabel: "Hiring organization" };
  }
  if (usageType === "individual_hiring") {
    return { role: "recruiter", roleLabel: "Individual hiring" };
  }
  return { role: "candidate", roleLabel: "Professional" };
};

const splitName = (value) => {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const hiringTypeFromUsageType = (usageType) => {
  if (usageType === "hiring_team") return "organization";
  if (usageType === "individual_hiring") return "individual";
  return "";
};

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const readStarterDraft = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem(starterStorageKey) || "null");
  } catch {
    return null;
  }
};

const getMissingWorkspaceNotice = (setupReason) => {
  if (setupReason === "first-time") {
    return {
      message: "Account setup needs to be completed",
      description: "We could not find your first workspace yet. Finish these steps to create it and continue into Olreadia.",
    };
  }

  if (setupReason === "repair") {
    return {
      message: "Workspace access needs to be restored",
      description: "We could not find an active workspace for this account. Complete these steps to create one and restore access.",
    };
  }

  return null;
};

const ProductOnboardingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const [stepIndex, setStepIndex] = useState(0);
  const [resumeApplied, setResumeApplied] = useState(false);
  const [resumeStepKey] = useState(() => {
    const draft = location.state?.onboardingDraft || readStarterDraft();
    return draft?.currentStepKey || "";
  });
  const [answers, setAnswers] = useState(() => {
    const draft = location.state?.onboardingDraft || readStarterDraft();
    if (!draft) return initialAnswers;
    const draftName = splitName(draft.workspaceName);
    return {
      email: draft.email || "",
      password: "",
      confirmPassword: "",
      useCase: draft.useCase || (
        draft.usageType === "hiring_team" || draft.usageType === "individual_hiring" ? "hiring" : ""
      ),
      hiringType: draft.hiringType || hiringTypeFromUsageType(draft.usageType),
      usageType: draft.usageType || "",
      workspaceName: draft.workspaceName || "",
      firstName: draft.firstName || draftName.firstName,
      lastName: draft.lastName || draftName.lastName,
      deliveryFocus: normalizeMultiValue(draft.deliveryFocus),
      credentialMethod: draft.credentialMethod || "",
      socialProvider: draft.socialProvider || "",
    };
  });
  const visibleQuestions = useMemo(() => getVisibleQuestions(answers), [answers]);
  const currentQuestion = visibleQuestions[Math.min(stepIndex, visibleQuestions.length - 1)];
  const currentTitle = currentQuestion.title;
  const currentDeck = currentQuestion.deck;
  const currentPlaceholder = currentQuestion.input?.placeholder || "";
  const currentColors = currentQuestion.colors || {
    eyebrow: OLIVEEB_COLORS.oliveebGreen,
    title: OLIVEEB_COLORS.oliveebDarkGreen,
    deck: OLIVEEB_COLORS.oliveebBlue,
    accent: OLIVEEB_COLORS.oliveebGreen,
    soft: OLIVEEB_COLORS.oliveebGreenSoft,
  };
  const currentValue = answers[currentQuestion.key];
  const currentOptions = getVisibleOptions(currentQuestion);
  const missingWorkspaceNotice =
    location.state?.source === "missing-workspace"
      ? getMissingWorkspaceNotice(location.state?.setupReason)
      : null;
  const isLastStep = stepIndex === visibleQuestions.length - 1;
  const canContinue = (() => {
    if (currentQuestion.contactEmail) {
      return isValidEmail(answers.email);
    }
    if (currentQuestion.key === "credentialMethod") {
      return Boolean(answers.credentialMethod);
    }
    if (currentQuestion.key === "loginCredentials") {
      return (
        isValidEmail(answers.email) &&
        String(answers.password || "").length >= 8 &&
        answers.password === answers.confirmPassword
      );
    }
    if (currentQuestion.multiple) {
      const visibleValues = currentOptions.map((option) => option.value);
      return normalizeMultiValue(currentValue).some((value) => visibleValues.includes(value));
    }
    if (currentQuestion.options) {
      return currentOptions.some((option) => option.value === currentValue);
    }
    return String(currentValue || "").trim().length > 0;
  })();
  const progress = Math.round(((stepIndex + 1) / visibleQuestions.length) * 100);

  const selectedLabels = useMemo(
    () =>
      visibleQuestions.reduce((summary, question) => {
        const value = answers[question.key];
        if (question.contactEmail) return summary;
        const visibleOptions = getVisibleOptions(question);
        if (question.multiple) {
          const labels = normalizeMultiValue(value)
            .map((item) => visibleOptions.find((option) => option.value === item)?.label)
            .filter(Boolean);
          if (labels.length) summary[question.key] = labels;
          return summary;
        }
        if (!value) return summary;
        const visibleOption = visibleOptions.find((option) => option.value === value);
        if (question.options && !visibleOption) return summary;
        summary[question.key] = visibleOption?.label || value;
        return summary;
      }, {}),
    [answers, visibleQuestions],
  );

  useEffect(() => {
    if (resumeApplied) return;
    if (!resumeStepKey) {
      setResumeApplied(true);
      return;
    }
    const resumedIndex = visibleQuestions.findIndex((question) => question.key === resumeStepKey);
    if (resumedIndex >= 0) {
      setStepIndex(resumedIndex);
    }
    setResumeApplied(true);
  }, [resumeApplied, resumeStepKey, visibleQuestions]);

  const buildOnboardingDraft = (finalAnswers, overrides = {}) => {
    const isHiring = finalAnswers.useCase === "hiring";
    const isOrganization = isHiring && finalAnswers.hiringType === "organization";
    const resolvedUsageType = isHiring
      ? (isOrganization ? "hiring_team" : "individual_hiring")
      : "professional";
    const resolvedDeliveryFocus = finalAnswers.useCase === "job_search"
      ? normalizeMultiValue(finalAnswers.deliveryFocus)
      : ["find_verified_talent"];
    const resolvedDeliveryFocusLabel = finalAnswers.useCase === "job_search"
      ? selectedLabels.deliveryFocus || []
      : ["Find verified talent"];
    const derivedRole = getDerivedRole(resolvedUsageType);
    const workspaceAccountType = isOrganization ? "ORGANIZATION" : "PERSONAL";
    const workspaceUse = isHiring ? "HIRING" : "JOB_SEARCH";
    const workspaceName = isOrganization
      ? finalAnswers.workspaceName.trim()
      : workspaceUse === "HIRING"
        ? "Personal hiring workspace"
        : "Personal job search workspace";
    const organizationName = isOrganization ? finalAnswers.workspaceName.trim() : "";

    return {
      source: "product-get-started",
      capturedAt: new Date().toISOString(),
      currentStepKey: overrides.currentStepKey || "",
      email: finalAnswers.email.trim(),
      useCase: finalAnswers.useCase,
      useCaseLabel: selectedLabels.useCase,
      hiringType: finalAnswers.hiringType,
      hiringTypeLabel: selectedLabels.hiringType,
      usageType: resolvedUsageType,
      usageTypeLabel: isHiring ? "For Hiring" : "For Job Search",
      workspaceName,
      firstName: "",
      lastName: "",
      organizationName,
      deliveryFocus: resolvedDeliveryFocus,
      deliveryFocusLabel: resolvedDeliveryFocusLabel,
      role: derivedRole.role,
      roleOther: "",
      roleLabel: derivedRole.roleLabel,
      credentialMethod: finalAnswers.credentialMethod,
      socialProvider: finalAnswers.socialProvider,
      workspaceCreation: {
        accountType: workspaceAccountType,
        workspaceUse,
        name: workspaceName,
        organizationName,
        onboardingIntent: workspaceUse,
        product: "OLIVEEB_OLREADIA_SERVICE",
      },
    };
  };

  useEffect(() => {
    if (!answers.useCase) return;
    try {
      window.localStorage.setItem(
        starterStorageKey,
        JSON.stringify(buildOnboardingDraft(answers, { currentStepKey: currentQuestion?.key })),
      );
    } catch {
      // Local resume is best-effort.
    }
  }, [answers, currentQuestion?.key]);

  const updateAnswer = (key, value) => {
    setAnswers((current) => ({
      ...current,
      [key]: value,
      ...(key === "useCase" && current.useCase !== value
        ? {
            hiringType: "",
            usageType: "",
            workspaceName: "",
            firstName: "",
            lastName: "",
            deliveryFocus: [],
            credentialMethod: "",
            socialProvider: "",
            email: "",
            password: "",
            confirmPassword: "",
          }
        : {}),
      ...(key === "hiringType" && current.hiringType !== value
        ? {
            workspaceName: "",
          }
        : {}),
      ...(key === "credentialMethod" && value !== "social_login"
        ? { socialProvider: "" }
        : {}),
      ...(key === "credentialMethod" && value !== "username_password"
        ? { email: "", password: "", confirmPassword: "" }
        : {}),
    }));
  };

  const toggleAnswer = (key, value) => {
    setAnswers((current) => {
      const selected = normalizeMultiValue(current[key]);
      return {
        ...current,
        [key]: selected.includes(value)
          ? selected.filter((item) => item !== value)
          : [...selected, value],
      };
    });
  };

  const selectOption = (key, value) => {
    updateAnswer(key, value);
    if (key === "credentialMethod") {
      if (value === "social_login") {
        finish({ credentialMethod: value, socialProvider: "" });
        return;
      }
      setStepIndex((index) => Math.min(index + 1, visibleQuestions.length));
    }
  };

  const finish = (overrides = {}) => {
    const finalAnswers = { ...answers, ...overrides };
    const onboardingDraft = buildOnboardingDraft(finalAnswers, { currentStepKey: "signup" });

    try {
      window.localStorage.setItem(starterStorageKey, JSON.stringify(onboardingDraft));
    } catch {
      // Route state still carries the draft when storage is unavailable.
    }

    navigate(paths.simpleSignup, {
      state: {
        ...(location.state || {}),
        onboardingDraft,
        signupCredentialMethod: finalAnswers.credentialMethod === "social_login" ? "social" : "form",
        signupSocialProvider: finalAnswers.credentialMethod === "social_login" ? finalAnswers.socialProvider : "",
        signupCredentials: finalAnswers.credentialMethod === "username_password"
          ? {
              email: finalAnswers.email.trim(),
              password: finalAnswers.password,
            }
          : null,
      },
    });
  };

  const goNext = () => {
    if (!canContinue) return;
    if (isLastStep) {
      finish();
      return;
    }
    setStepIndex((index) => Math.min(index + 1, visibleQuestions.length - 1));
  };

  const goBack = () => {
    if (stepIndex === 0) {
      navigate("/", { state: location.state });
      return;
    }
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const resetOnboardingFlow = () => {
    try {
      window.localStorage.removeItem(starterStorageKey);
    } catch {
      // Local storage cleanup is best-effort.
    }
    setAnswers(initialAnswers);
    setStepIndex(0);
    setResumeApplied(true);
    navigate(paths.getStarted, { replace: true, state: null });
  };

  const goToSignIn = () => {
    try {
      window.localStorage.removeItem(starterStorageKey);
    } catch {
      // Local storage cleanup is best-effort.
    }
    navigate(paths.simpleLogin, { state: null });
  };

  const goToSignOut = () => {
    try {
      window.localStorage.removeItem(starterStorageKey);
    } catch {
      // Local storage cleanup is best-effort.
    }
    logout(false, paths.simpleLogin);
  };

  return (
    <AuthShell mode="center">
      <style>{onboardingStyles}</style>
      <Card
        variant="borderless"
        className="olreadia-onboarding-card"
        style={{ width: "min(100%, 620px)" }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              shape="circle"
              style={{
                color: OLIVEEB_COLORS.oliveebBlue,
                borderColor: OLIVEEB_COLORS.oliveebBlue,
                background: OLIVEEB_COLORS.white,
                boxShadow: "0 10px 22px rgba(0,87,184,0.12)",
              }}
            />
            <div
              style={{
                padding: "7px 14px",
                borderRadius: 24,
                background: OLIVEEB_COLORS.white,
                border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                boxShadow: "0 12px 24px rgba(39,174,48,0.14)",
              }}
            >
              <AuthBrand centered size={78} />
            </div>
            <Space size={8}>
              <Button
                type="link"
                onClick={resetOnboardingFlow}
                style={{ color: OLIVEEB_COLORS.textMuted, fontWeight: 900, paddingInline: 0 }}
              >
                Reset
              </Button>
              <Button
                type="link"
                onClick={isAuthenticated ? goToSignOut : goToSignIn}
                style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 900, paddingInline: 0 }}
              >
                {isAuthenticated ? "Sign out" : "Sign in"}
              </Button>
            </Space>
          </div>

          <Progress
            percent={progress}
            showInfo={false}
            strokeColor={currentColors.accent}
            trailColor={currentColors.soft}
          />

          {missingWorkspaceNotice ? (
            <Alert
              showIcon
              type={location.state?.setupReason === "repair" ? "warning" : "info"}
              message={missingWorkspaceNotice.message}
              description={missingWorkspaceNotice.description}
              style={{
                borderRadius: 18,
                borderColor:
                  location.state?.setupReason === "repair"
                    ? OLIVEEB_COLORS.goldBorder
                    : OLIVEEB_COLORS.oliveebBlueSoft,
                background:
                  location.state?.setupReason === "repair"
                    ? "linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)"
                    : "linear-gradient(135deg, #F2F8FF 0%, #FFFFFF 100%)",
              }}
            />
          ) : null}

          <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center" }}>
            <Text
              style={{
                color: currentColors.eyebrow,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontSize: 11,
              }}
            >
              {currentQuestion.eyebrow}
            </Text>
            <Title
              level={2}
              style={{
                color: OLIVEEB_COLORS.oliveebGreen,
                margin: 0,
                fontWeight: 900,
                lineHeight: 1.12,
              }}
            >
              {currentTitle}
            </Title>
            <Paragraph
              style={{
                margin: 0,
                color: currentColors.deck,
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {currentDeck}
            </Paragraph>
          </Space>

          {currentQuestion.credentialsForm ? (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Input
                className="olreadia-onboarding-input"
                value={answers.email}
                placeholder="Email username"
                autoComplete="email"
                inputMode="email"
                autoFocus
                prefix={<MailOutlined style={{ color: currentColors.accent }} />}
                onChange={(event) => updateAnswer("email", event.target.value)}
                onPressEnter={goNext}
              />
              <Input.Password
                className="olreadia-onboarding-input"
                value={answers.password}
                placeholder="Password"
                autoComplete="new-password"
                prefix={<LockOutlined style={{ color: currentColors.accent }} />}
                onChange={(event) => updateAnswer("password", event.target.value)}
                onPressEnter={goNext}
              />
              <Input.Password
                className="olreadia-onboarding-input"
                value={answers.confirmPassword}
                placeholder="Confirm password"
                autoComplete="new-password"
                prefix={<LockOutlined style={{ color: currentColors.accent }} />}
                onChange={(event) => updateAnswer("confirmPassword", event.target.value)}
                onPressEnter={goNext}
              />
            </Space>
          ) : currentQuestion.contactEmail ? (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <Input
                className="olreadia-onboarding-input"
                value={answers.email}
                placeholder="you@company.com"
                autoComplete="email"
                inputMode="email"
                autoFocus
                onChange={(event) => updateAnswer("email", event.target.value)}
                onPressEnter={goNext}
              />
            </Space>
          ) : currentQuestion.options ? (
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {currentOptions.map((option) => {
                const selected = currentQuestion.multiple
                  ? normalizeMultiValue(currentValue).includes(option.value)
                  : currentValue === option.value;
                return (
                  <Button
                    key={option.value}
                    className="olreadia-onboarding-option"
                    icon={option.icon}
                    onClick={() =>
                      currentQuestion.multiple
                        ? toggleAnswer(currentQuestion.key, option.value)
                        : selectOption(currentQuestion.key, option.value)
                    }
                    style={{
                      borderColor: selected
                        ? currentColors.accent
                        : currentColors.soft,
                      background: selected
                        ? `linear-gradient(135deg, ${currentColors.soft} 0%, #FFFFFF 100%)`
                        : OLIVEEB_COLORS.white,
                      color: selected
                        ? currentColors.title
                        : OLIVEEB_COLORS.oliveebDarkBlue,
                    }}
                  >
                    <Space direction="vertical" size={2}>
                      <Text style={{ color: "inherit", fontWeight: 900, fontSize: 16 }}>
                        {option.label}
                      </Text>
                      <Text style={{ color: "inherit", fontSize: 12, opacity: 0.82 }}>
                        {option.caption}
                      </Text>
                    </Space>
                    {selected ? (
                      <CheckCircleOutlined
                        style={{
                          marginLeft: "auto",
                          color: currentColors.accent,
                          fontSize: 18,
                        }}
                      />
                    ) : null}
                  </Button>
                );
              })}
            </Space>
          ) : (
            <Input
              className="olreadia-onboarding-input"
              value={formatLabelValue(currentValue)}
              maxLength={currentQuestion.input.maxLength}
              placeholder={currentPlaceholder}
              autoFocus
              onChange={(event) => updateAnswer(currentQuestion.key, event.target.value)}
              onPressEnter={goNext}
            />
          )}

          <Button
            type="primary"
            size="large"
            block
            disabled={!canContinue}
            icon={isLastStep ? <CheckCircleOutlined /> : <ArrowRightOutlined />}
            onClick={goNext}
            style={{
              height: 50,
              borderRadius: 999,
              fontWeight: 900,
              fontSize: 16,
              background: canContinue
                ? `linear-gradient(135deg, ${currentColors.accent} 0%, ${OLIVEEB_COLORS.oliveebGreen} 100%)`
                : currentColors.soft,
              borderColor: canContinue
                ? currentColors.accent
                : currentColors.soft,
              boxShadow: canContinue
                ? "0 14px 28px rgba(16,147,87,0.22)"
                : "none",
            }}
          >
            {isLastStep ? "Continue to signup" : "Continue"}
          </Button>
        </Space>
      </Card>
    </AuthShell>
  );
};

export default ProductOnboardingPage;
